import { useState, useEffect } from "react";
import axios from "axios";
import { Play, X } from "lucide-react";
import { SiSpotify, SiApplemusic, SiYoutube } from "react-icons/si";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Portfolio = () => {
  const [tracks, setTracks] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/tracks`)
      .then((res) => {
        if (mounted) setTracks(res.data || []);
      })
      .catch(() => {
        if (mounted) setTracks([]);
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="relative py-28 lg:py-36 bg-gradient-to-b from-black via-[#070707] to-black"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div>
            <p
              data-testid="portfolio-eyebrow"
              className="font-body text-xs uppercase tracking-[0.3em] text-brand-cyan"
            >
              [ Selected Work ]
            </p>
            <h2
              data-testid="portfolio-headline"
              className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white"
            >
              The Catalog.
            </h2>
          </div>
          <p className="max-w-md font-body text-white/60">
            Tap any cover to play the official video. Streaming links live on
            every card.
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="aspect-square rounded-3xl border border-white/10 bg-white/[0.03] animate-pulse"
              />
            ))}
          </div>
        ) : tracks.length === 0 ? (
          <p className="text-white/50 font-body">No tracks yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {tracks.map((track) => (
              <article
                key={track.id}
                data-testid={`portfolio-card-${track.id}`}
                className="group relative rounded-3xl overflow-hidden border border-white/10 bg-white/[0.02] transition-all duration-500 hover:-translate-y-2"
              >
                <button
                  type="button"
                  data-testid={`portfolio-play-${track.id}`}
                  onClick={() => setActive(track)}
                  className="block w-full text-left"
                  aria-label={`Play ${track.title}`}
                >
                  <div className="relative aspect-square overflow-hidden">
                    <img
                      src={track.art}
                      alt={`${track.title} cover art`}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent opacity-90" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <div className="h-20 w-20 rounded-full bg-white text-black flex items-center justify-center shadow-2xl shadow-brand-magenta/40">
                        <Play size={28} className="ml-1" fill="black" />
                      </div>
                    </div>
                  </div>
                </button>

                <div className="absolute bottom-0 inset-x-0 p-6 lg:p-7">
                  <p className="font-body text-[10px] uppercase tracking-[0.3em] text-white/60">
                    {track.artist}
                  </p>
                  <h3 className="mt-2 font-display font-extrabold text-xl lg:text-2xl tracking-tight text-white leading-tight">
                    {track.title}
                  </h3>
                  <div className="mt-4 flex items-center gap-3">
                    {track.spotify && (
                      <a
                        href={track.spotify}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`portfolio-spotify-${track.id}`}
                        aria-label={`Open ${track.title} on Spotify`}
                        className="h-9 w-9 rounded-full bg-white/10 border border-white/15 backdrop-blur-md flex items-center justify-center text-white hover:bg-[#1DB954] hover:border-[#1DB954] transition-all"
                      >
                        <SiSpotify size={16} />
                      </a>
                    )}
                    {track.apple && (
                      <a
                        href={track.apple}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`portfolio-apple-${track.id}`}
                        aria-label={`Open ${track.title} on Apple Music`}
                        className="h-9 w-9 rounded-full bg-white/10 border border-white/15 backdrop-blur-md flex items-center justify-center text-white hover:bg-brand-magenta hover:border-brand-magenta transition-all"
                      >
                        <SiApplemusic size={16} />
                      </a>
                    )}
                    <a
                      href={track.youtube}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`portfolio-youtube-${track.id}`}
                      aria-label={`Watch ${track.title} on YouTube`}
                      className="h-9 w-9 rounded-full bg-white/10 border border-white/15 backdrop-blur-md flex items-center justify-center text-white hover:bg-red-600 hover:border-red-600 transition-all"
                    >
                      <SiYoutube size={16} />
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      {/* YouTube modal */}
      {active && (
        <div
          data-testid="portfolio-video-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md p-4"
          onClick={() => setActive(null)}
        >
          <div
            className="relative w-full max-w-5xl aspect-video rounded-2xl overflow-hidden border border-white/10 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              data-testid="portfolio-modal-close"
              aria-label="Close video"
              onClick={() => setActive(null)}
              className="absolute -top-12 right-0 h-10 w-10 rounded-full bg-white text-black flex items-center justify-center hover:bg-brand-magenta hover:text-white transition-colors"
            >
              <X size={18} />
            </button>
            <iframe
              src={`https://www.youtube.com/embed/${active.youtube_id}?autoplay=1`}
              title={active.title}
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          </div>
        </div>
      )}
    </section>
  );
};
